/**
 * The two derived caption strings.
 *
 * `human_final_caption` seeds the editable final caption by applying every span
 * operation; `human_marked_caption_with_edits` is the XML-ish research artefact.
 *
 * Note the server stores whatever the client sends and never recomputes these - the
 * final caption is hand-editable after the operations apply, which is why it does not
 * always equal the derivation (true for 9 of the 20 legacy records).
 */

import { humanAddedSpans, remapSpans } from './remap.js';
import {
  collectSpans,
  markupTagOf,
  noteOf,
  noteSuffix,
  operationOf,
  replacementOf,
  TAG_META,
} from './spans.js';

/** Collapse runs of spaces and tidy space-before-punctuation, preserving paragraphs. */
export function normalizeWhitespace(text) {
  return String(text || '')
    .split(/\n{2,}/)
    .map((paragraph) =>
      paragraph
        .replace(/[ \t]+/g, ' ')
        .replace(/[ \t]+([,.;:!?])/g, '$1')
        .trim(),
    )
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

function escapeAttribute(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Apply every span operation to the working caption.
 * remove -> drop, replace -> substitute, keep_with_uncertainty_note -> `text <note>`.
 */
export function buildFinalCaption(workingCaption, spans) {
  const src = String(workingCaption || '');
  const ordered = collectSpans(spans).filter((s) => s.end_char <= src.length);

  let out = '';
  let cursor = 0;

  for (const span of ordered) {
    if (span.start_char < cursor) continue; // overlapping: first writer wins
    out += src.slice(cursor, span.start_char);

    const raw = src.slice(span.start_char, span.end_char);
    const operation = operationOf(span);
    const replacement = replacementOf(span);

    if (operation === 'replace' && replacement) {
      out += replacement;
    } else if (operation === 'keep_with_uncertainty_note' || span.label === 'uncertain') {
      // An `uncertain` span always carries its note through, whatever the operation.
      // The note is the whole point of the label, and the legacy export did the same.
      out += raw + noteSuffix(noteOf(span));
    }
    // 'remove' contributes nothing.

    cursor = span.end_char;
  }
  out += src.slice(cursor);

  return normalizeWhitespace(out);
}

/**
 * The final caption PLUS the ranges to highlight in it.
 *
 * The old tool rendered the final caption with colour, not as plain text: you could see
 * at a glance which words were substituted and which carried an uncertainty note. This
 * rebuilds that.
 *
 * Offsets are tracked while the string is assembled, then moved onto the whitespace-
 * normalized text with the same re-anchoring code the editor uses - normalizing changes
 * the string, so raw offsets would land in the wrong place.
 */
export function buildFinalCaptionWithRanges(workingCaption, spans) {
  const src = String(workingCaption || '');
  const ordered = collectSpans(spans).filter((s) => s.end_char <= src.length);

  let raw = '';
  let cursor = 0;
  const rawRanges = [];

  for (const span of ordered) {
    if (span.start_char < cursor) continue;
    raw += src.slice(cursor, span.start_char);

    const original = src.slice(span.start_char, span.end_char);
    const operation = operationOf(span);
    const replacement = replacementOf(span);

    if (operation === 'replace' && replacement) {
      const start = raw.length;
      raw += replacement;
      rawRanges.push({
        start_char: start,
        end_char: raw.length,
        text: replacement,
        label: span.label,
        kind: 'replacement',
        original,
      });
    } else if (operation === 'keep_with_uncertainty_note' || span.label === 'uncertain') {
      const start = raw.length;
      raw += original + noteSuffix(noteOf(span));
      rawRanges.push({
        start_char: start,
        end_char: raw.length,
        text: raw.slice(start),
        label: span.label,
        kind: 'note',
        original,
      });
    }
    // 'remove' contributes nothing, so nothing is highlighted.

    cursor = span.end_char;
  }
  raw += src.slice(cursor);

  const text = normalizeWhitespace(raw);
  const { spans: moved } = remapSpans(rawRanges, raw, text);

  return { text, ranges: moved };
}

/**
 * The marked-up caption, e.g.
 *   `a small strainer<wrong_uncertainty> or pan</wrong_uncertainty>`
 *   `<hallucination><edit operation="replace" replacement="between">around</edit></hallucination>`
 */
export function buildMarkedCaption(workingCaption, spans) {
  const src = String(workingCaption || '');
  const ordered = collectSpans(spans)
    .filter((s) => s.end_char <= src.length)
    .sort((a, b) => a.start_char - b.start_char || b.end_char - a.end_char);

  if (!ordered.length) return src;

  const opens = new Map();
  const closes = new Map();
  for (const span of ordered) {
    opens.set(span.start_char, [...(opens.get(span.start_char) || []), span]);
    closes.set(span.end_char, [...(closes.get(span.end_char) || []), span]);
  }

  const points = [...new Set([0, src.length, ...opens.keys(), ...closes.keys()])].sort(
    (a, b) => a - b,
  );

  const out = [];
  let cursor = 0;
  for (const point of points) {
    out.push(src.slice(cursor, point));

    // Close innermost first (the latest-starting span).
    for (const span of (closes.get(point) || []).sort((a, b) => b.start_char - a.start_char)) {
      if (operationOf(span) === 'keep_with_uncertainty_note' || span.label === 'uncertain') {
        out.push(noteSuffix(noteOf(span)));
      }
      if (operationOf(span) === 'replace' && replacementOf(span)) out.push('</edit>');
      out.push(`</${markupTagOf(span)}>`);
    }
    // Open outermost first (the longest span).
    for (const span of (opens.get(point) || []).sort((a, b) => b.end_char - a.end_char)) {
      out.push(`<${markupTagOf(span)}>`);
      if (operationOf(span) === 'replace' && replacementOf(span)) {
        out.push(`<edit operation="replace" replacement="${escapeAttribute(replacementOf(span))}">`);
      }
    }
    cursor = point;
  }
  out.push(src.slice(cursor));

  return out.join('');
}


/**
 * One caption showing every edit at once, for reading rather than for storage.
 *
 * The stored captions each answer a different question - the working caption is what was
 * tagged, the final caption is what the edits produced - so neither shows a removal AND
 * its replacement AND the note that was attached. Reading an annotation means holding
 * two captions side by side and inferring the difference.
 *
 * This builds the single view instead: original wording struck through where it was
 * removed, the replacement inserted right after it, and an uncertainty note in brackets
 * where one was written. It is DISPLAY ONLY - never store it, never send it, and never
 * measure offsets against it, because the text it returns is not any caption that exists.
 *
 * @returns {Array<{text:string, className?:string, title?:string}>}
 */
export function buildReviewSegments(workingCaption, spans, { agentCaption = null } = {}) {
  const src = String(workingCaption || '');
  const ordered = collectSpans(spans).filter((s) => s.end_char <= src.length);

  // Text the annotator typed lives in the WORKING caption - they edit it directly - so
  // it is found by diffing against the caption the agent produced, exactly as the
  // editor's own added-text highlight does. Without this the combined view silently
  // omits whole clauses of the annotator's work.
  const added = agentCaption ? humanAddedSpans(agentCaption, src) : [];
  const isAdded = (from, to) =>
    added.some((a) => from < a.end_char && a.start_char < to);

  const out = [];

  /** Push a slice of the working caption, split wherever added text starts or ends. */
  const pushSource = (from, to, className, title) => {
    if (to <= from) return;
    const cuts = new Set([from, to]);
    for (const a of added) {
      if (a.start_char > from && a.start_char < to) cuts.add(a.start_char);
      if (a.end_char > from && a.end_char < to) cuts.add(a.end_char);
    }
    const points = [...cuts].sort((a, b) => a - b);
    for (let i = 0; i < points.length - 1; i += 1) {
      const [a, b] = [points[i], points[i + 1]];
      const wasAdded = isAdded(a, b);
      out.push({
        text: src.slice(a, b),
        className: [className, wasAdded ? 'review-added' : ''].filter(Boolean).join(' ') || undefined,
        title: wasAdded ? [title, 'added by the annotator'].filter(Boolean).join(' · ') : title,
      });
    }
  };

  /** Push text that is NOT from the working caption, so it can never be "added". */
  const pushNew = (text, className, title) => {
    if (text) out.push({ text, className, title });
  };

  let cursor = 0;
  for (const span of ordered) {
    if (span.start_char < cursor) continue;
    pushSource(cursor, span.start_char);

    const original = src.slice(span.start_char, span.end_char);
    const operation = operationOf(span);
    const replacement = replacementOf(span);
    const tagClass = TAG_META[span.label]?.className || '';
    const tagName = TAG_META[span.label]?.label || span.label;

    if (operation === 'replace' && replacement) {
      pushSource(span.start_char, span.end_char, `${tagClass} review-struck`, `${tagName}: replaced`);
      pushNew(replacement, 'review-inserted', `Replaced \u201c${original}\u201d`);
    } else if (operation === 'keep_with_uncertainty_note' || span.label === 'uncertain') {
      pushSource(span.start_char, span.end_char, tagClass, `${tagName}: kept with a note`);
      pushNew(noteSuffix(noteOf(span)), 'review-note', 'Uncertainty note');
    } else {
      pushSource(span.start_char, span.end_char, `${tagClass} review-struck`, `${tagName}: removed`);
    }

    cursor = span.end_char;
  }
  pushSource(cursor, src.length);

  return out;
}
